import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  motion,
  useMotionValue,
  useSpring,
  useVelocity,
  useTransform,
} from "framer-motion";
import { isAndroid, isIOS } from "../capacitor";
import { hapticTabSelection } from "../haptics";

/* ─── TabButton ─────────────────────────────────────────────────────────────
   Separate component so useTransform hooks are called at the top level of a
   function component, never inside a .map() loop.
   Scale is derived purely from MotionValues — zero re-renders during drag.
────────────────────────────────────────────────────────────────────────────── */
function TabButton({ t, idx, count, springX, barWidthMV, darkMode }) {
  const tabCenter = useTransform(barWidthMV, (bw) =>
    bw > 0 ? (idx + 0.5) * (bw / count) : 0
  );
  const dist = useTransform(
    [springX, tabCenter],
    ([sx, cx]) => Math.abs(sx - cx)
  );
  const scaleMV = useTransform([dist, barWidthMV], ([d, bw]) => {
    const tw = bw / count;
    if (tw <= 0) return t.active ? 1.05 : 1;
    return 1.2 - (Math.min(d, tw) / tw) * 0.25;
  });

  // Editorial palette — unified warm gold, matching Radio + Ibadah hubs
  const accentColor = darkMode ? "#ffb300" : "#b87008";
  const mutedColor  = darkMode ? "#5a7387" : "#8293a3";
  const badgeColor  = t.badgeRed ? "#ff4444" : (darkMode ? "#ffb300" : "#b87008");

  // Primary tap path. Goes through React's document-root delegated click
  // system — completely separate from the bar's setPointerCapture / drag
  // listeners — so taps still switch tabs even if the bar's pointer
  // machinery gets confused (stuck isDragging, missed pointerup, iOS
  // synthetic cancels, etc). Calling t.onClick when already on that tab
  // is a no-op, so this safely coexists with the drag-through-tabs path
  // which also calls t.onClick during pointermove.
  const handleTap = (e) => {
    // Stop bubbling so the bar's own listeners don't also see a click and
    // double-haptic. The bar's pointer listeners are unaffected (different
    // event family). The bar's onUp call to t.onClick remains as a backup.
    e.stopPropagation();
    if (typeof t.onClick === "function") t.onClick();
  };
  return (
    <motion.div
      data-tab-id={t.id}
      onClick={handleTap}
      role="button"
      aria-label={t.label}
      aria-current={t.active ? "page" : undefined}
      style={{
        flex: 1,
        padding: "16px 4px 14px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
        touchAction: "manipulation",
        position: "relative",
        zIndex: 1,
        scale: scaleMV,
        cursor: "pointer",
      }}
    >
      <span style={{
        fontSize: "20px",
        lineHeight: 1,
        display: "block",
        color: t.active ? accentColor : mutedColor,
        transition: "color 0.25s",
        fontFamily: '"Instrument Serif", Georgia, serif',
        fontWeight: t.active ? 700 : 500,
      }}>
        {t.icon}
      </span>
      <span
        style={{
          fontSize: "10px",
          fontWeight: "700",
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          pointerEvents: "none",
          color: t.active ? accentColor : mutedColor,
          transition: "color 0.25s",
          fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
          whiteSpace: "nowrap",
        }}
      >
        {t.label}
      </span>
      {t.active && (
        <span aria-hidden style={{
          position: "absolute",
          bottom: 9,
          left: "50%",
          transform: "translateX(-50%)",
          width: 14,
          height: 1,
          background: accentColor,
          opacity: 0.75,
        }}/>
      )}
      {t.badge && (
        <span
          style={{
            position: "absolute",
            top: "8px",
            right: "calc(50% - 18px)",
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            background: badgeColor,
            boxShadow: t.badgeRed ? "0 0 0 2px rgba(255,68,68,0.25)" : `0 0 0 2px ${darkMode ? "rgba(255,179,0,0.25)" : "rgba(184,112,8,0.22)"}`,
            animation: "pulse 1.5s ease-in-out infinite",
          }}
        />
      )}
    </motion.div>
  );
}

/* ─── TabBar ─────────────────────────────────────────────────────────────────
   Apple Music-style liquid tab bar.
   • Bubble tracks finger continuously via useMotionValue (no state, no re-renders).
   • Spring lag (damping 25 / stiffness 180 / mass 0.6) creates the liquid feel.
   • useVelocity → scaleX stretches the bubble in the direction of movement.
   • Each tab icon magnifies based on distance to the bubble center.
   • On release: bubble springs to the exact center of the active tab.
   • App-switcher coexistence: touches starting in the bottom 30 px of the
     screen (the iOS home-indicator zone) skip pointer capture so iOS can
     still detect the swipe-to-switch-app gesture. Touches higher on the
     pill are claimed by us for tab navigation.
────────────────────────────────────────────────────────────────────────────── */

// Touches this close to the screen bottom are left to iOS (app-switcher zone).
// Android does not have this gesture zone — applying it there would block
// taps since the tab bar sits at bottom:0 on Android.
const IOS_GESTURE_ZONE = 30; // px

export default function TabBar({ tabs, darkMode, hidden }) {
  const barRef         = useRef(null);
  const isDragging     = useRef(false);
  const activeSegRef   = useRef(-1);
  const hasSwitchedTab = useRef(false);
  const capturedPointerId = useRef(null);
  const tabsRef        = useRef(tabs);
  const count          = tabs.length;

  useEffect(() => { tabsRef.current = tabs; }, [tabs]);

  // ── Core motion values ─────────────────────────────
  const rawX       = useMotionValue(0);
  const springX    = useSpring(rawX, { damping: 25, stiffness: 180, mass: 0.6 });
  const vX         = useVelocity(springX);
  const barWidthMV = useMotionValue(0);

  // ── Derived: bubble geometry ───────────────────────
  const bubbleScaleX = useTransform(vX, [-2000, 0, 2000], [1.12, 1, 1.12]);
  const bubbleWidthMV = useTransform(barWidthMV, (bw) =>
    Math.max(0, bw / count - 6)
  );
  const bubbleLeftEdge = useTransform(
    [springX, barWidthMV],
    ([sx, bw]) => (bw > 0 ? sx - (bw / count - 6) / 2 : -1000)
  );

  // ── ResizeObserver ─────────────────────────────────
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (w > 0) barWidthMV.set(w);
    });
    ro.observe(el);
    const initial = el.getBoundingClientRect().width;
    if (initial > 0) barWidthMV.set(initial);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Snap bubble when active tab changes externally ─
  const activeIdx = tabs.findIndex((t) => t.active);
  useEffect(() => {
    if (isDragging.current) return;
    const bw = barWidthMV.get();
    if (bw <= 0 || count <= 0) return;
    const center = (activeIdx + 0.5) * (bw / count);
    rawX.set(center);
    springX.jump(center);
  }, [activeIdx, count]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Global safety net ──────────────────────────────
  // If pointerup/pointercancel fires outside the captured element (iOS WKWebView
  // and some Android WebView edge cases can swallow element-level events),
  // ensure drag state never stays permanently stuck.
  //
  // CRITICAL: only act on events whose pointerId matches the one we captured.
  // iOS fires synthetic pointercancel events (pointerId=0, empty pointerType)
  // during system interruptions — notification pull, control center peek,
  // gesture rejections. Reacting to those during an in-flight real touch
  // resets isDragging mid-tap and the user's pointerup silently no-ops.
  useEffect(() => {
    const safeReset = (e) => {
      if (!isDragging.current) return;
      const ours = capturedPointerId.current;
      if (ours != null && e?.pointerId !== ours) return; // foreign pointer — ignore
      isDragging.current     = false;
      hasSwitchedTab.current = false;
      activeSegRef.current   = -1;
    };
    window.addEventListener("pointerup",     safeReset);
    window.addEventListener("pointercancel", safeReset);
    return () => {
      window.removeEventListener("pointerup",     safeReset);
      window.removeEventListener("pointercancel", safeReset);
    };
  }, []);

  // ── Native pointer event listeners ────────────────
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const getX = (e) => {
      const rect = el.getBoundingClientRect();
      return Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    };

    const getSeg = (x, bw, cnt) =>
      bw > 0 ? Math.min(Math.floor(x / (bw / cnt)), cnt - 1) : 0;

    const onDown = (e) => {
      // Always reset stale drag state before any early-return checks so a
      // previously stuck hasSwitchedTab / isDragging can never persist into
      // the next interaction.
      isDragging.current     = false;
      hasSwitchedTab.current = false;
      activeSegRef.current   = -1;

      // NOTE: the original IOS_GESTURE_ZONE early-return was REMOVED —
      // with the midnight pill positioned ~10pt from the viewport bottom,
      // roughly 20pt of the pill overlapped iOS's home-swipe zone and
      // taps there silently dropped. iOS handles its system gestures at
      // the OS level regardless of what the WebView captures, so the
      // defensive skip was doing nothing except breaking our own taps.

      const x = getX(e);
      isDragging.current = true;
      capturedPointerId.current = e.pointerId;
      springX.jump(x);
      rawX.set(x);
      const bw  = barWidthMV.get();
      const cnt = tabsRef.current.length;
      activeSegRef.current = getSeg(x, bw, cnt);
      try { el.setPointerCapture(e.pointerId); } catch {}
      // Idle watchdog — if WKWebView swallows pointerup/cancel (known quirk
      // when system gestures interleave), force-clear drag state after 1s
      // without any further pointer activity. Without this, the bar can
      // appear "dead" until the app is relaunched.
      bumpIdleTimer();
    };

    const onMove = (e) => {
      if (!isDragging.current) return;
      bumpIdleTimer();
      const x   = getX(e);
      rawX.set(x);
      const bw  = barWidthMV.get();
      if (!bw) return;
      const cnt = tabsRef.current.length;
      const seg = getSeg(x, bw, cnt);
      if (seg !== activeSegRef.current) {
        activeSegRef.current = seg;
        const t = tabsRef.current[seg];
        if (t && !t.active) {
          hapticTabSelection();
          t.onClick();
          hasSwitchedTab.current = true;
        }
      }
    };

    const onUp = (e) => {
      clearIdleTimer();
      if (!isDragging.current) { releaseCapture(e); return; }
      isDragging.current = false;

      const seg = activeSegRef.current;
      const cnt = tabsRef.current.length;
      const bw  = barWidthMV.get();

      // Pure tap: activate the segment the finger lifted from
      if (!hasSwitchedTab.current && seg >= 0 && seg < cnt) {
        const t = tabsRef.current[seg];
        if (t) {
          hapticTabSelection();
          t.onClick();
        }
      }

      // Settle bubble at the final segment (use seg directly — tabsRef may be stale)
      if (seg >= 0 && bw > 0)
        rawX.set((seg + 0.5) * (bw / cnt));

      activeSegRef.current   = -1;
      hasSwitchedTab.current = false;
      releaseCapture(e);
    };

    // pointercancel = iOS stole the gesture (app-switcher swipe, system gesture).
    // Just reset state and spring the bubble back — never activate a tab.
    // Ignore synthetic cancels (pid=0 / empty type) and cancels for foreign
    // pointer ids — iOS fires those during system interruptions and they'd
    // otherwise kill our in-flight real touch mid-tap.
    const onCancel = (e) => {
      const ours = capturedPointerId.current;
      const synthetic = !e?.pointerId && !e?.pointerType;
      const foreign = ours != null && e?.pointerId !== ours;
      if (synthetic || foreign) return;
      clearIdleTimer();
      releaseCapture(e);
      if (!isDragging.current) return;
      isDragging.current     = false;
      hasSwitchedTab.current = false;
      activeSegRef.current   = -1;
      // Spring bubble back to the currently-active tab
      const activeTabIdx = tabsRef.current.findIndex(t => t.active);
      if (activeTabIdx >= 0) {
        const bw = barWidthMV.get();
        if (bw > 0) rawX.set((activeTabIdx + 0.5) * (bw / tabsRef.current.length));
      }
    };

    // Explicitly release pointer capture so a hijacked-then-released system
    // gesture can't leave us holding a phantom capture. iOS's
    // releasePointerCapture is idempotent; missing pointer ids no-op.
    const releaseCapture = (e) => {
      const pid = e?.pointerId ?? capturedPointerId.current;
      if (pid == null) return;
      try { if (el.hasPointerCapture?.(pid)) el.releasePointerCapture(pid); } catch {}
      capturedPointerId.current = null;
    };

    // Idle watchdog — if no pointermove arrives for 1s while a drag is
    // outstanding, assume WKWebView dropped the up/cancel and force-reset.
    let idleTimer = null;
    const clearIdleTimer = () => { if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; } };
    const bumpIdleTimer = () => {
      clearIdleTimer();
      idleTimer = setTimeout(() => {
        if (!isDragging.current) return;
        isDragging.current     = false;
        hasSwitchedTab.current = false;
        activeSegRef.current   = -1;
        const pid = capturedPointerId.current;
        if (pid != null) {
          try { if (el.hasPointerCapture?.(pid)) el.releasePointerCapture(pid); } catch {}
          capturedPointerId.current = null;
        }
      }, 1000);
    };

    el.addEventListener("pointerdown",   onDown);
    el.addEventListener("pointermove",   onMove, { passive: false });
    el.addEventListener("pointerup",     onUp);
    el.addEventListener("pointercancel", onCancel);

    return () => {
      clearIdleTimer();
      el.removeEventListener("pointerdown",   onDown);
      el.removeEventListener("pointermove",   onMove);
      el.removeEventListener("pointerup",     onUp);
      el.removeEventListener("pointercancel", onCancel);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard detection ────────────────────────────
  // In browser, vv.height shrinks while innerHeight stays — delta > 150 works.
  // In Capacitor native iOS, the WKWebView frame itself shrinks when the
  // keyboard opens, so BOTH innerHeight and vv.height drop by the same amount.
  // innerHeight - vv.height stays near 0 and never crosses 150. Fix: track
  // the maximum seen vv.height as a stable baseline; compare against that.
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = vv.height;
    const update = () => {
      if (vv.height > baseline) baseline = vv.height; // grow baseline on expand / rotate
      setKeyboardVisible(baseline - vv.height > 150);
    };
    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, []);

  // ── Android nav-bar inset ──────────────────────────
  // env(safe-area-inset-bottom) is unreliable in Android Chrome/TWA — some
  // versions return 0 even when the viewport extends behind the nav bar.
  // We measure it via a JS probe; if it's still 0 and the screen ratio
  // suggests edge-to-edge rendering, we fall back to 48 px (standard
  // 3-button nav bar height). The result is published as --nav-inset so
  // scroll-content padding can compensate via CSS without double-counting.
  const isIOSDevice = isIOS() || /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroidUA = !isIOSDevice && /Android/.test(navigator.userAgent);

  const [navInset, setNavInset] = useState(0);
  // iOSEnvPx — measured pixel value of env(safe-area-inset-bottom). On
  // Capacitor WKWebView this reliably reports ~34pt (home indicator). On
  // iOS Safari PWA it can be inflated by Safari's bottom-chrome reserve
  // (commonly 80+pt) — we use the measured value to decide whether to
  // trust env() or fall back to a hardcoded 34pt home-indicator estimate.
  const [iosEnvPx, setIosEnvPx] = useState(34);

  useEffect(() => {
    function measure() {
      // Probe: read env(safe-area-inset-bottom) as a computed pixel value.
      const probe = document.createElement("div");
      probe.style.cssText =
        "position:fixed;bottom:0;left:0;right:0;height:0;" +
        "padding-bottom:env(safe-area-inset-bottom,0px);" +
        "pointer-events:none;visibility:hidden;z-index:-1;";
      document.body.appendChild(probe);
      const envPx = probe.getBoundingClientRect().height;
      document.body.removeChild(probe);

      if (isIOSDevice) {
        // Clamp to a sane physical range: a home indicator is ~34pt on
        // notched iPhones, 20pt on iPad, 0 elsewhere. Anything past 50pt
        // is Safari chrome inflation — ignore the value entirely and
        // assume a 34pt home indicator (the common case for the devices
        // this PWA actually ships to).
        let cleaned = envPx;
        if (cleaned > 50) cleaned = 34;
        if (cleaned < 0) cleaned = 0;
        setIosEnvPx(cleaned);
        return;
      }

      if (!isAndroidUA) return;

      let inset = envPx;
      if (inset === 0) {
        // env() returned 0 — check if viewport looks edge-to-edge by comparing
        // innerHeight to screen.height. Non-edge-to-edge shrinks by ~72 px
        // (24 status + 48 nav); edge-to-edge only shrinks by ~24 px (status).
        const ratio = window.innerHeight / window.screen.height;
        if (ratio > 0.93) inset = 48; // standard 3-button nav bar height
      }
      setNavInset(inset);
      // Publish as CSS variable so scroll-content can add extra clearance
      // only in the fallback case (env() already handles itself in CSS).
      document.documentElement.style.setProperty(
        "--nav-inset",
        envPx > 0 ? "0px" : `${inset}px`
      );
    }

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isAndroidUA, isIOSDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hidden || keyboardVisible) return null;

  // Apple Music / iOS floating-tab-bar placement: pill sits low within the
  // home-indicator safe-area, with a glass strip filling the rest of that
  // zone below it. Dropped ~24pt below the home-indicator height so the
  // bar feels rooted to the bottom, not floating too high.
  //   iPhone notched  → bottom ≈ 34 - 24 = 10pt from viewport bottom
  //   iPhone SE / iPad → bottom ≈ 20 - 24 → clamped to 0
  //   Android          → sits on the nav bar (or screen bottom edge-to-edge)
  //
  // Why we use a JS-measured iosEnvPx instead of raw CSS env():
  //   On Capacitor WKWebView (TestFlight) env(safe-area-inset-bottom) is a
  //   stable 34pt (home indicator). On iOS Safari PWA the same env() can
  //   be inflated by Safari's bottom-chrome reserve (URL bar / toolbar),
  //   returning ~80pt even when the chrome is hidden. The probe at mount
  //   measures the value, ignores anything >50pt as Safari noise, and
  //   falls back to a 34pt assumption — so the bar lands at the same 10pt
  //   resting position on both targets regardless of what Safari reports.
  const bottomOffset = isIOSDevice
    ? `${Math.max(0, iosEnvPx - 24)}px`
    : `${navInset}px`;

  // Portal to document.body so the bar escapes the `.app` transformed
  // stacking context. With a `transform: scale(...)` ancestor (.app holds
  // scale(1) idle / 1.04 for the fan menu), iOS WKWebView positions
  // `position: fixed` children RELATIVE to the transformed box rather than
  // the viewport. When the visual viewport drifts from the layout viewport
  // (scroll bounce, rubber-band) the bar's visual position diverges from
  // its hit-test region — taps land on whatever scroll content is at the
  // same DOM coordinate. Portal anchors fixed positioning to the viewport
  // again. No geometry change; only DOM mount location.
  return createPortal((
    <>
      {/* Safe-area fill — opaque glass strip below the pill so scrolled
          content doesn't peek through the home-indicator zone. Matches
          the pill's glass so it reads as one continuous surface. */}
      <div aria-hidden style={{
        position: "fixed",
        left: 0, right: 0, bottom: 0,
        // iosEnvPx is the JS-probed safe-area value clamped to a sane
        // physical range — matches bottomOffset above. Using a measured
        // pixel literal avoids Safari PWA's inflated env() reading
        // (chrome-reserve area can be 80+pt).
        height: isIOSDevice ? `${iosEnvPx}px` : `${navInset}px`,
        background: darkMode ? "rgba(14,10,6,0.85)" : "rgba(247,236,216,0.90)",
        backdropFilter: "blur(28px) saturate(200%)",
        WebkitBackdropFilter: "blur(28px) saturate(200%)",
        borderTop: darkMode ? "1px solid rgba(255,179,0,0.10)" : "1px solid rgba(184,112,8,0.12)",
        zIndex: 99,
        pointerEvents: "none",
      }}/>
    <motion.div
      ref={barRef}
      style={{
        position: "fixed",
        bottom: bottomOffset,
        left: 0,
        right: 0,
        margin: "0 auto",
        width: "calc(100% - 16px)",
        maxWidth: "480px",
        zIndex: 100,
        borderRadius: "32px",
        // Warm "paper" glass — matches Ibadah Featured hero + Inspiration card backgrounds
        background: darkMode ? "rgba(14,10,6,0.80)" : "rgba(247,236,216,0.86)",
        backdropFilter: "blur(28px) saturate(200%)",
        WebkitBackdropFilter: "blur(28px) saturate(200%)",
        border: `1px solid ${
          darkMode ? "rgba(255,179,0,0.14)" : "rgba(184,112,8,0.16)"
        }`,
        boxShadow: darkMode
          ? "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)"
          : "0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
        display: "flex",
        alignItems: "center",
        padding: "8px",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        overflow: "hidden",
      }}
    >
      {/* Dashed hairline top — echoes the dashed footer on the Inspiration card */}
      <div aria-hidden style={{
        position: "absolute",
        top: 0,
        left: 18,
        right: 18,
        height: 1,
        borderTop: darkMode
          ? "1px dashed rgba(255,179,0,0.14)"
          : "1px dashed rgba(184,112,8,0.18)",
        pointerEvents: "none",
      }}/>
      {/* Liquid blob bubble — driven entirely by MotionValues, no state */}
      <motion.div
        style={{
          position: "absolute",
          left: 0,
          top: "8px",
          height: "calc(100% - 16px)",
          width: bubbleWidthMV,
          x: bubbleLeftEdge,
          scaleX: bubbleScaleX,
          transformOrigin: "center center",
          borderRadius: "24px",
          filter: "blur(14px)",
          background: darkMode
            ? "rgba(255,179,0,0.12)"
            : "rgba(184,112,8,0.10)",
          border: `1px solid ${
            darkMode ? "rgba(255,179,0,0.22)" : "rgba(184,112,8,0.18)"
          }`,
          boxShadow: darkMode
            ? "0 0 28px rgba(255,179,0,0.18), inset 0 1px 0 rgba(255,255,255,0.06)"
            : "0 0 24px rgba(184,112,8,0.18), inset 0 1px 0 rgba(255,255,255,0.5)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {tabs.map((t, idx) => (
        <TabButton
          key={t.id}
          t={t}
          idx={idx}
          count={count}
          springX={springX}
          barWidthMV={barWidthMV}
          darkMode={darkMode}
        />
      ))}
    </motion.div>
    </>
  ), document.body);
}
